import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import Select from 'react-select';

import {
  analyticsViewsInitialDataRequest,
  analyticsViewsReportRequest,
  analyticsViewsResetErrorState,
} from '/actions/actions';

const REDUCER_NAME = 'analytics';

// Универсальная функция для генерации CSV как файла
// в целом можно использовать для других случаев (возможно перенести в утилиты)
const downloadCSV = (content) => {
  const blob = new Blob([content], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  const url = URL.createObjectURL(blob);
  link.href = url;
  link.setAttribute('download', 'report.csv');
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

const reportPage = () => {
  // Коннектимся к редаксу
  const dispatch = useDispatch();
  const data = useSelector((state) => state[REDUCER_NAME]);

  // Список перменных локального стейта
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [dateSuggestions, setDateSuggestions] = useState([]);
  const [regionOptions, setRegionOptions] = useState([]);
  const [selectedRegion, setSelectedRegion] = useState({});
  const [typeOptions, setTypeOptions] = useState([]);
  const [selectedTypes, setSelectedTypes] = useState([]);
  const [needActors, setNeedActors] = useState(false);
  const [canBeGenerated, setCanBeGenerated] = useState(false);

  // Сеттер для дат в формате массива
  const setDates = ([start, end]) => {
    setStartDate(start);
    setEndDate(end);
  };

  // Запашиваем начальные данные когда диспетчер готов
  useEffect(
    () => {
      dispatch(analyticsViewsInitialDataRequest());
    },
    [dispatch],
  );

  // Функция расставления начальных данных
  const setInitalData = (initial) => {
    if (initial?.dates?.length) {
      setDates(initial.dates.at(-1));
      setDateSuggestions(initial.dates);
    }
    if (initial?.regions?.length) {
      const opt = initial.regions.map((region) => ({
        value: region.id,
        label: region.name,
      }));
      setRegionOptions(opt);
      setSelectedRegion(opt[0]);
    }
    if (initial?.types?.length) {
      const opt = initial.types.map((type, idx) => ({
        value: idx,
        label: type,
      }));
      setTypeOptions(opt);
    }
    if (
      initial?.dates?.length
      && initial?.regions?.length
      && initial?.types?.length
    ) {
      setCanBeGenerated(true);
    }
  };

  // Следим за изменением стейта
  useEffect(
    () => {
      if (!canBeGenerated) {
        setInitalData(data);
      }

      if (data?.viewsReport) {
        downloadCSV(data.viewsReport);
      }
    },
    [data],
  );

  // Выравниваем дату начала, если дата конца меньше
  useEffect(
    () => {
      if (new Date(startDate) > new Date(endDate)) {
        setStartDate(endDate);
      }
    },
    [endDate],
  );

  // Выравниваем дату конца, если дата начала больше
  useEffect(
    () => {
      if (new Date(endDate) < new Date(startDate)) {
        setEndDate(startDate);
      }
    },
    [startDate],
  );

  // Отправляем запрос на сервер для получения отчёта
  const requestReportByData = (e) => {
    e.preventDefault();

    if (!selectedRegion?.value
      || !startDate
      || !endDate
    ) {
      console.error('Не все поля заполнены', {
        selectedRegion,
        startDate,
        endDate,
      });
      return;
    }

    const reportOptions = {
      regionID: selectedRegion?.value,
      types: selectedTypes.map((type) => `'${type.label}'`),
      dateStart: startDate,
      dateEnd: endDate,
      needActors,
    };

    dispatch(analyticsViewsReportRequest(reportOptions));
  };

  return (
    <div className="ui justified container" style={{ paddingTop: '20px' }}>
      <form
        className="ui small form"
        action="#"
        onSubmit={requestReportByData}
      >
        <div className="ui stacked segment left aligned">
          <h3 className="ui dividing header">
            Отчёт о просмотрах
          </h3>

          {startDate && endDate && (
            <>
              <div className="field">
                <div className="two fields">
                  <div className="field">
                    <label htmlFor="start_date">
                      Дата начала:
                    </label>
                    <input
                      type="date"
                      id="start_date"
                      name="start_date"
                      value={startDate}
                      onChange={(e) => {
                        setStartDate(e.target.value);
                      }}
                    />
                  </div>

                  <div className="field">
                    <label htmlFor="end_date">
                      Дата конца:
                    </label>
                    <input
                      type="date"
                      id="end_date"
                      name="end_date"
                      value={endDate}
                      onChange={(e) => {
                        setEndDate(e.target.value);
                      }}
                    />
                  </div>
                </div>
              </div>
            </>
          )}

          <div className="ui celled horizontal list">
            { dateSuggestions?.length > 0 && dateSuggestions.map(([start, end, name]) => (
              <div
                className="item"
                style={{
                  cursor: 'pointer',
                  padding: '10px',
                  borderBottom: '1px solid #ccc',
                  backgroundColor: startDate === start && endDate === end ? '#f0f0f0' : 'white',
                  borderRadius: '4px',
                }}
                role="button"
                tabIndex={0}
                onClick={() => {
                  setDates([start, end]);
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    setDates([start, end]);
                  }
                }}
                key={`date-${name}`}
              >
                {name}
              </div>
            ))}
          </div>

          {regionOptions?.length > 0 && (
            <>
              <div className="ui divider" />
              <div className="grouped fields">
                <label htmlFor="type">Регион:</label>
                <Select
                  defaultValue={regionOptions[0]}
                  name="regions"
                  options={regionOptions}
                  placeholder="по-умолчанию все"
                  value={selectedRegion}
                  onChange={(selected) => {
                    setSelectedRegion(selected);
                  }}
                />
              </div>
              <div className="ui divider" />
            </>
          )}

          {typeOptions?.length > 0 && (
            <>
              <div className="grouped fields">
                <label htmlFor="type">Тип материалов:</label>
                <Select
                  defaultValue={[]}
                  isMulti
                  name="types"
                  options={typeOptions}
                  placeholder="по-умолчанию все"
                  onChange={(selected) => {
                    setSelectedTypes(selected);
                  }}
                />
              </div>
              <div className="ui divider" />
            </>
          )}

          {canBeGenerated && (
            <>
              <div className="field">
                <div className="ui checkbox">
                  <input
                    type="checkbox"
                    id="need_actors"
                    name="need_actors"
                    className="hidden"
                    checked={needActors}
                    onChange={(e) => {
                      setNeedActors(e.target.checked);
                    }}
                  />
                  <label htmlFor="need_actors">
                    Добавить акторов (creator, editor, publisher)
                  </label>
                </div>
              </div>
              <div className="ui divider" />
            </>
          )}

          {canBeGenerated && (
            <button className="ui button blue" type="submit">
              Создать отчёт о просмотрах
            </button>
          )}

          {data?.error && (
            <div className="ui red message">
              <i
                className="icon close"
                onClick={() => dispatch(analyticsViewsResetErrorState())}
                aria-label="Close"
                role="button"
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    dispatch(analyticsViewsResetErrorState());
                  }
                }}
              />
              {data.error}
            </div>
          )}
        </div>
      </form>
    </div>
  );
};

export default reportPage;
